import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { Control } from 'react-hook-form'
import FormField, { FormGrid } from '../../../components/ui/FormField'
import FormDateField from '../../../components/ui/form/FormDateField'
import FormTimeRangeField from '../../../components/ui/form/FormTimeRangeField'
import FormDropdownField from '../../../components/ui/form/FormDropdownField'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useDisplayName } from '../../../auth/useCurrentUser'
import { useMeetingGroupOptions } from '../hooks/useMeetingGroupOptions'
import type { MinutesFormValues } from '../schema'

interface Props {
  control: Control<MinutesFormValues>
  /** Katılımcılar + Tutanaklar alt-bölümleri (Temel Bilgiler kartının içinde h3 alt-başlık — item 3). */
  children?: ReactNode
}

/**
 * Toplantı temel bilgileri bölümü (039, US1). Grup (aranabilir dropdown), ad, tarih (Calendar), saat
 * (Calendar timeOnly, 24 saat), yer + Tutanak Düzenleyen (giriş yapan kullanıcı; salt-okunur/pasif — FR-008).
 * Tarih+saat gönderimde tek {@code meetingDate}'e birleştirilir (sayfada).
 */
export default function MeetingInfoSection({ control, children }: Readonly<Props>) {
  const { t } = useTranslation()
  const groups = useMeetingGroupOptions()
  const editor = useDisplayName() ?? ''
  const options = (groups.data ?? []).map((g) => ({ label: g.name, value: g.id }))

  return (
    <section className="minutes-section">
      <div className="minutes-section-head">
        <h2>{t('minutes.info.title')}</h2>
      </div>
      <FormGrid>
        <FormDropdownField
          control={control}
          name="meetingGroupId"
          label={t('minutes.info.group')}
          options={options}
          required
          filter
          placeholder={t('minutes.info.groupPlaceholder')}
        />
        <FormTextField
          control={control}
          name="name"
          label={t('minutes.info.name')}
          required
          maxLength={250}
          placeholder={t('minutes.info.namePlaceholder')}
        />
        <FormDateField control={control} name="meetingDate" label={t('minutes.info.date')} required />
        <FormTimeRangeField
          control={control}
          startName="meetingStartTime"
          endName="meetingEndTime"
          label={t('minutes.info.time')}
          startPlaceholder={t('minutes.info.start')}
          endPlaceholder={t('minutes.info.end')}
          required
        />
        <FormTextField
          control={control}
          name="meetingLocation"
          label={t('minutes.info.location')}
          required
          maxLength={250}
          placeholder={t('minutes.info.locationPlaceholder')}
        />
        <FormField label={t('minutes.info.editor')} htmlFor="minutes-editor">
          <input
            id="minutes-editor"
            className="p-inputtext p-component p-disabled"
            value={editor}
            disabled
            readOnly
          />
        </FormField>
      </FormGrid>
      {children}
    </section>
  )
}
